import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { eventTracking } from '@/services/event-tracking';
import { EntityPage } from '@/shared/models/entity-page';

import { IDriver } from '../model/driver';
import { IDriverWorkload } from '../model/driver-workload';
import { IReferralCode } from '../model/referral-code';
import { namespace as driverNamespace } from '../namespace';

const initialState: {
  driver: IDriver | null;
  myDriver: IDriver;
  driverReferralCode: IReferralCode;
  all: IDriver[];
  drivers: EntityPage<IDriver>;
  driverWorkload: IDriverWorkload | null;
} = {
  driverReferralCode: {
    code: '',
    description: '',
  },
  driver: null,
  myDriver: {
    hasContractWithPartner: true,
    id: '',
    partnerId: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    birthday: null,
    gender: null,
    licenseNumber: '',
    ukNationalInsurance: '',
    homeAddress: '',
    rating: 0,
    score: 0,
    docUploads: {},
    testStatuses: {},
    accessStatus: 'pending',
    licensingAuthority: null,
    cityId: '',
    onlineStatus: 'offline',
    hasPassedAccreditation: false,
  },
  all: [],
  drivers: {
    items: [],
    total: 0,
    limit: 20,
    offset: 0,
  },
  driverWorkload: null,
};

const driversSlice = createSlice({
  name: driverNamespace,
  initialState,
  reducers: {
    setReferralCode: (state, action: PayloadAction<IReferralCode>) => {
      state.driverReferralCode = action.payload;
    },
    setDriverWorkload: (state, action: PayloadAction<IDriverWorkload>) => {
      state.driverWorkload = action.payload;
    },
    setDriver: (state, action: PayloadAction<IDriver>) => {
      state.driver = action.payload;

      const idx = state.all.findIndex(d => d.id === action.payload.id);

      state.all[idx] = action.payload;
    },
    resetDriver: state => {
      state.driver = null;
    },
    setAllDrivers: (state, action: PayloadAction<IDriver[]>) => {
      state.all = action.payload;
    },
    setDrivers: (state, action: PayloadAction<EntityPage<IDriver>>) => {
      state.drivers = action.payload;
    },
    setMyDriver: (state, action: PayloadAction<IDriver>) => {
      state.myDriver = action.payload;

      eventTracking.setDriverId(action.payload.id);
    },
    updateMyDriver: (state, action: PayloadAction<Partial<IDriver>>) => {
      state.myDriver = {
        ...state.myDriver,
        ...action.payload,
      };
    },
    updateDriver: (state, action: PayloadAction<{ id: string; update: Partial<IDriver> }>) => {
      if (state.driver && state.driver.id === action.payload.id) {
        state.driver = {
          ...state.driver,
          ...action.payload.update,
        };
      }
    },
  },
});

export { driversSlice, driverNamespace };
